import { useToast } from '@app/core/notifications';
import { useRouter } from '@mi-front/core';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export interface DeleteConfirmDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onDelete: () => Promise<void>;
  readonly title?: string;
  readonly message: ReactNode;
  readonly successMessage?: ReactNode;
  readonly redirectTo?: string;
}

/**
 * Confirmation dialog that runs a delete action, shows toast feedback, and optionally redirects.
 */
export function DeleteConfirmDialog({
  open,
  onClose,
  onDelete,
  title,
  message,
  successMessage,
  redirectTo,
}: DeleteConfirmDialogProps) {
  const { formatMessage } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const { history } = useRouter();
  const [isDeleting, setIsDeleting] = useState(false);

  async function handleDelete() {
    try {
      setIsDeleting(true);
      await onDelete();
      notifySuccess(
        successMessage || (
          <FormattedMessage
            id="common.notifications.deleted"
            defaultMessage="Eliminado correctamente"
          />
        ),
      );
      if (redirectTo) {
        history.replace(redirectTo);
      }
    } catch (err: unknown) {
      notifyError(
        <FormattedMessage
          id="common.notifications.deleteError"
          defaultMessage="Error al eliminar"
        />,
      );
      throw err;
    } finally {
      setIsDeleting(false);
      onClose();
    }
  }

  function handleClose() {
    if (!isDeleting) {
      onClose();
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm">
      {title && <DialogTitle>{title}</DialogTitle>}
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isDeleting}>
          {formatMessage({
            id: 'common.actions.cancel',
            defaultMessage: 'Cancelar',
          })}
        </Button>
        <Button onClick={handleDelete} loading={isDeleting} color="error" variant="contained">
          {formatMessage({
            id: 'common.actions.delete',
            defaultMessage: 'Eliminar',
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
